import { CommonModule, DatePipe, DecimalPipe } from '@angular/common'
import { BreakpointObserver } from '@angular/cdk/layout'
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http'
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIcon } from '@angular/material/icon'
import { MatTableModule } from '@angular/material/table'
import { MatCheckbox } from '@angular/material/checkbox'
import {
  MatSnackBar,
  MatSnackBarRef,
  TextOnlySnackBar,
} from '@angular/material/snack-bar'
import { MatDialog } from '@angular/material/dialog'
import { MatMenuModule } from '@angular/material/menu'
import { MatDivider } from '@angular/material/divider'
import {
  ActivatedRoute,
  convertToParamMap,
  ParamMap,
  Params,
  Router,
  RouterLink,
} from '@angular/router'
import { Title } from '@angular/platform-browser'

import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import {
  Observable,
  ReplaySubject,
  Subject,
  catchError,
  debounceTime,
  filter,
  finalize,
  from,
  ignoreElements,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { ButtonComponent } from '../../components/button/button.component'
import {
  ContextMenu,
  ContextMenuComponent,
} from '../../components/context-menu/context-menu.component'
import { LoadingComponent } from '../../components/loading/loading.component'
import {
  DialogResult,
  MessageDialogService,
} from '../../components/message-dialog/message-dialog.service'
import { SearchInputComponent } from '../../components/search-input/search-input.component'
import {
  Item,
  MultipleSelectComponent,
} from '../../components/multiple-select/multiple-select.component'
import { AssessmentSummary } from '../../services/assessment/assessment.mapping'
import { CsvExportService } from '../../services/csv-export/csv-export.service'
import { assertGraphqlData } from '../../util/assertGraphqlData'
import { AssessmentStore } from '../../stores/assessment.store'
import { AuthService } from '../../services/auth.service'
import { UserStore } from '../../stores/user.store'
import { ReportToOption, TagMatchMode } from '../../../_generated/graphql'
import { UserService } from '../../services/user/user.service'
import { getTitle } from '../../util/accessibility'
import { MOBILE_BREAKPOINT } from '../../util/breakpoints'
import {
  ExportAssessmentCsvComponent,
  ExportAssessmentCsvDialogData,
  ExportAssessmentCsvDialogResult,
} from './export-assessment-csv/export-assessment-csv.component'
import { RenameBusinessMeetingComponent } from '../assessment/home/rename-business-meeting/rename-business-meeting.component'
import {
  CustomLabelService,
  LABEL_KEYS,
} from '../../services/custom-label/custom-label.service'
import {
  ExportIntegratedAssessmentComponent,
  ExportIntegratedAssessmentDialogResult,
} from './export-integrated-assessment/export-integrated-assessment.component'
import { IntegratedAssessmentExportService } from '../../services/integrated-assessment-export/integrated-assessment-export.service'
import {
  downloadBlob,
  getFileNameFromContentDisposition,
} from '../../util/blob-download'
import {
  EditAssessmentTagsComponent,
  EditAssessmentTagsDialogData,
  EditAssessmentTagsDialogResult,
} from './edit-assessment-tags/edit-assessment-tags.component'

type AssessmentRow = AssessmentSummary & {
  selected: boolean
}

const MAXIMUM_SELECTION_COUNT = 50
const DEFAULT_SORT_FIELD = 'businessMeetingRecordDate'
const DEFAULT_SORT_DIRECTION = 'desc'
const SORT_FIELDS = [
  'userName',
  'userDepartmentName',
  'businessMeetingName',
  'businessMeetingRecordDate',
  'accountName',
]

/** サーバー起因エラー（5xx・通信失敗）に出す汎用メッセージ。時間をおけば解消しうる */
const EXPORT_RETRIABLE_ERROR_MESSAGE =
  'エクスポートに失敗しました。時間をおいて再度お試しください。'

/** リクエスト起因エラー（4xx）に出す汎用メッセージ。再試行しても解消しない */
const EXPORT_CLIENT_ERROR_MESSAGE =
  'エクスポートできませんでした。指定した条件やネットワーク設定をご確認ください。'

/** 400 レスポンスボディ（JSON 文字列）から error メッセージを取り出す。形式が違えば null */
function extractServerErrorMessage(text: string): string | null {
  try {
    const parsed: unknown = JSON.parse(text)
    if (
      parsed != null &&
      typeof parsed === 'object' &&
      'error' in parsed &&
      typeof parsed.error === 'string'
    ) {
      return parsed.error
    }
  } catch {
    // ボディが JSON でない場合は汎用メッセージにフォールバックする
  }
  return null
}

@UntilDestroy()
@Component({
  selector: 'app-assessment-list-page',
  standalone: true,
  imports: [
    ButtonComponent,
    MatIcon,
    MatTableModule,
    MatCheckbox,
    MatMenuModule,
    DatePipe,
    DecimalPipe,
    SearchInputComponent,
    MultipleSelectComponent,
    CommonModule,
    FormsModule,
    LoadingComponent,
    InfiniteScrollDirective,
    ContextMenuComponent,
    MatDivider,
    RouterLink,
  ],
  templateUrl: './assessment-list-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './assessment-list-page.component.scss',
})
export class AssessmentListPageComponent implements OnInit {
  @ViewChild('tableContainer') tableContainer: ElementRef | undefined
  @ViewChild(SearchInputComponent, { static: true })
  private searchInput!: SearchInputComponent

  readonly PAGE_SIZE_LIMIT = 100
  offset = 0

  private readonly desktopColumns: string[] = [
    'select',
    'userName',
    'userDepartmentName',
    'businessMeetingName',
    'businessMeetingRecordDate',
    'accountName',
    'averageScore',
    'action',
  ]
  private readonly desktopColumnsWithTags: string[] = [
    'select',
    'userName',
    'userDepartmentName',
    'businessMeetingName',
    'businessMeetingRecordDate',
    'accountName',
    'tags',
    'averageScore',
    'action',
  ]
  private readonly mobileColumns: string[] = [
    'userName',
    'userDepartmentName',
    'businessMeetingName',
    'businessMeetingRecordDate',
    'accountName',
    'averageScore',
  ]
  private readonly mobileColumnsWithTags: string[] = [
    'userName',
    'userDepartmentName',
    'businessMeetingName',
    'businessMeetingRecordDate',
    'accountName',
    'tags',
    'averageScore',
  ]
  displayedColumns: string[] = this.desktopColumns
  isMobile = false
  dataSource: AssessmentRow[] = []

  sortField = DEFAULT_SORT_FIELD
  sortDirection: 'asc' | 'desc' = DEFAULT_SORT_DIRECTION

  loading = true
  exportingIntegrated = false
  private exportSnackBarRef: MatSnackBarRef<TextOnlySnackBar> | null = null
  private readonly assessmentSearch$ = new Subject<void>()

  private readonly tagMastersLoaded$ = new ReplaySubject<void>(1)
  private tagMastersLoaded = false
  /** 自画面が要求した URL 条件。タグマスター取得待ちの間も記録する。 */
  private requestedQueryParams: ParamMap | null = null
  /** 最後に検索を要求した条件。タグマスター取得待ちの間は更新しない。 */
  private lastQueryParams: ParamMap | null = null
  readonly queryNameInput$ = new Subject<string>()
  queryName = ''
  tagItems: Item[] = []
  readonly tagMatchOptions = [
    { value: TagMatchMode.Any, label: 'いずれかを含む' },
    { value: TagMatchMode.All, label: 'すべてを含む' },
  ] as const
  tagMatch: TagMatchMode = TagMatchMode.Any
  reportToOptions: ReportToOption[] = []
  reportToUserId = ''
  private reportToOptionsLoaded = false

  get hasAssessmentTagMasters(): boolean {
    return this.tagItems.length > 0
  }

  // デフォルト値はBEで取得しているためここでは空文字を設定
  overallScoreLabel = ''

  private readonly baseContextMenu: ContextMenu[] = [
    {
      icon: 'edit',
      title: '名前を変更',
      type: 'RENAME',
    },
  ]
  private readonly editTagsContextMenu: ContextMenu = {
    icon: 'label',
    title: 'タグを編集',
    type: 'EDIT_TAGS',
  }
  private readonly deleteContextMenu: ContextMenu = {
    icon: 'delete',
    title: '削除',
    type: 'DELETE',
  }

  get contextMenu(): ContextMenu[] {
    return this.hasAssessmentTagMasters
      ? [
          ...this.baseContextMenu,
          this.editTagsContextMenu,
          this.deleteContextMenu,
        ]
      : [...this.baseContextMenu, this.deleteContextMenu]
  }

  get checkedCount(): number {
    return this.dataSource.filter((row) => row.selected)?.length
  }
  get checkedState(): 'CHECKED' | 'UNCHECKED' | 'INDETERMINATE' {
    const checkedCount =
      this.dataSource.filter((assessment) => assessment.selected)?.length ?? 0
    if (checkedCount === 0) {
      return 'UNCHECKED'
    } else if (checkedCount === this.dataSource.length) {
      return 'CHECKED'
    } else {
      return 'INDETERMINATE'
    }
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private assessmentStore: AssessmentStore,
    private csvExportService: CsvExportService,
    private messageDialogService: MessageDialogService,
    private authService: AuthService,
    private userStore: UserStore,
    private titleService: Title,
    private customLabelService: CustomLabelService,
    private integratedAssessmentExportService: IntegratedAssessmentExportService,
    private breakpointObserver: BreakpointObserver,
    private userService: UserService,
  ) {
    this.titleService.setTitle(getTitle('アセスメント一覧'))
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([MOBILE_BREAKPOINT])
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.isMobile = result.matches
        this.updateDisplayedColumns()
      })

    this.customLabelService
      .getLabel(LABEL_KEYS.OVERALL_SCORE)
      .pipe(untilDestroyed(this))
      .subscribe((value) => (this.overallScoreLabel = value))

    this.assessmentStore
      .getAssessmentTagMasters()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (tagMasters) => {
          this.tagItems = tagMasters.map((tagMaster) => ({
            id: tagMaster.id,
            label: tagMaster.name,
            checked: false,
          }))
          this.updateDisplayedColumns()
          this.completeTagMastersLoading()
        },
        error: (error: unknown) => {
          console.error('Failed to load assessment tag masters:', error)
          this.tagItems = []
          this.updateDisplayedColumns()
          if (this.route.snapshot.queryParamMap.has('tagId')) {
            this.messageDialogService
              .showError(
                'タグを取得できなかったため、タグの絞り込みを解除して表示しています。',
              )
              .pipe(untilDestroyed(this))
              .subscribe()
          }
          this.completeTagMastersLoading()
        },
      })

    this.observeAssessmentSearches()
    this.queryNameInput$
      .pipe(debounceTime(300), untilDestroyed(this))
      .subscribe((queryName) => this.queryNameChange(queryName))
    this.observeQueryParams()
    this.loadReportToOptions()
  }

  /** 行クリックで詳細へ遷移する。修飾キー付きクリックはリンクのブラウザ標準動作に委ねるため何もしない。 */
  navigateToHome(event: MouseEvent, userId: string, assessmentId: string) {
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      return
    }

    this.router.navigate(['/home'], {
      queryParams: { 'user-id': userId, 'assessment-id': assessmentId },
    })
  }

  queryNameChange(queryName: string) {
    if (queryName === this.queryName) {
      return
    }
    this.offset = 0
    this.queryName = queryName
    this.updateQueryParams()
    this.loadAssessments()
  }

  /** タグ絞り込みを更新し、先頭ページから再取得する */
  changeSelectedTags(items: Item[]): void {
    const previousTagIds = this.selectedTagIds()
    this.tagItems = items
    const tagIds = this.selectedTagIds()
    // 未選択のまま一致条件だけが変わった場合は結果が変わらないため URL だけ更新する
    if (previousTagIds == null && tagIds == null) {
      this.updateQueryParams()
      return
    }
    this.offset = 0
    this.dataSource = []
    this.updateQueryParams()
    this.loadAssessments()
  }

  /** タグの一致条件を更新する。再取得は直後の changeSelectedTags で行う */
  changeTagMatch(mode: string): void {
    if (!Object.values(TagMatchMode).includes(mode as TagMatchMode)) {
      throw new Error('タグの一致条件が不正です')
    }
    this.tagMatch = mode as TagMatchMode
  }

  /** 上司の絞り込みを更新し、先頭ページから再取得する。 */
  changeReportTo(reportToUserId: string): void {
    this.reportToUserId = reportToUserId
    this.offset = 0
    this.dataSource = []
    this.updateQueryParams()
    this.loadAssessments()
  }

  /**
   * 候補を一度だけ取得し、無効な上司や取得失敗時の上司の選択を解除する。
   * 取得失敗は一覧の表示を妨げないよう通知せず、ドロップダウンを非表示（候補 0 件）のままにする。
   */
  private loadReportToOptions(): void {
    this.userService
      .getReportToOptions()
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        next: (options) => {
          this.reportToOptions = options
          this.reportToOptionsLoaded = true
          if (
            this.reportToUserId !== '' &&
            !this.isValidReportTo(this.reportToUserId)
          ) {
            this.changeReportTo('')
          }
        },
        error: (error: unknown) => {
          console.error('Failed to load report-to options:', error)
          if (this.reportToUserId !== '') {
            this.changeReportTo('')
          }
        },
      })
  }

  /** 候補取得前は URL の値を維持し、取得後だけ候補との一致を検証する。 */
  private isValidReportTo(userId: string): boolean {
    return (
      !this.reportToOptionsLoaded ||
      this.reportToOptions.some((option) => option.userId === userId)
    )
  }

  sort(sortField: string) {
    this.loading = true
    if (this.sortField === sortField) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      this.sortDirection = 'asc'
      this.sortField = sortField
    }
    this.offset = 0
    this.updateQueryParams()
    this.loadAssessments()
  }

  async loadMoreAssessments() {
    if (this.loading) {
      return
    }

    this.offset += 1
    this.loadAssessments()
  }

  sleep = async (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms))

  exportAssessments() {
    this.matDialog
      .open<
        ExportAssessmentCsvComponent,
        ExportAssessmentCsvDialogData,
        ExportAssessmentCsvDialogResult
      >(ExportAssessmentCsvComponent, {
        data: { csvType: 'KeyActionEvaluations' },
      })
      .afterClosed()
      .pipe(
        tap((result: ExportAssessmentCsvDialogResult | undefined) => {
          if (!result) {
            return
          }

          const PAGE_SIZE = 500
          let offset = 0
          let allAssessments: {
            userName: string
            userEmail: string
            departmentName?: string | null
            reportToName?: string | null
            businessMeetingTitle: string
            accountName?: string | null
            businessMeetingRecordDate: Date
            keyActionName: string
            score: number
            evaluation: string
            skillMapName: string
            businessMeetingId: string
            tagNames: string
          }[] = []
          this.loading = true

          const fetchNextPage = () => {
            this.assessmentStore
              .exportAssessments(
                { limit: PAGE_SIZE, offset },
                {
                  startDate: result.startDate,
                  endDate: result.endDate,
                },
              )
              .pipe(
                tap((res) => {
                  assertGraphqlData(res)
                  const assessments = res.data.exportAssessments
                  allAssessments = allAssessments.concat(assessments)

                  // PAGE_SIZE件未満しか返ってこなかったら終了
                  if (assessments.length < PAGE_SIZE) {
                    const hasTags = allAssessments.some(
                      (a) => a.tagNames !== '',
                    )
                    const headers: {
                      label: string
                      key: keyof (typeof allAssessments)[number]
                    }[] = [
                      { label: '名前', key: 'userName' },
                      { label: 'メール', key: 'userEmail' },
                      { label: '部署', key: 'departmentName' },
                      { label: 'マネージャー', key: 'reportToName' },
                      { label: '商談', key: 'businessMeetingTitle' },
                      { label: '商談日', key: 'businessMeetingRecordDate' },
                      { label: '顧客名', key: 'accountName' },
                      { label: 'キーアクション', key: 'keyActionName' },
                      { label: 'スコア', key: 'score' },
                      { label: '評価コメント', key: 'evaluation' },
                      { label: 'スキルマップ名', key: 'skillMapName' },
                      { label: '商談ID', key: 'businessMeetingId' },
                    ]
                    if (hasTags) {
                      headers.push({ label: 'タグ', key: 'tagNames' })
                    }
                    this.csvExportService.downloadCsv(
                      'assessments.csv',
                      headers,
                      allAssessments,
                    )
                    this.loading = false
                  } else {
                    // 次のページを取得（負荷低減のため1秒間隔を空ける）
                    offset += PAGE_SIZE
                    setTimeout(() => {
                      fetchNextPage()
                    }, 1000)
                  }
                }),
                catchError(() => {
                  this.loading = false
                  return of()
                }),
              )
              .subscribe()
          }

          fetchNextPage()
        }),
      )
      .subscribe()
  }

  exportAssessmentSummaries() {
    this.matDialog
      .open<
        ExportAssessmentCsvComponent,
        ExportAssessmentCsvDialogData,
        ExportAssessmentCsvDialogResult
      >(ExportAssessmentCsvComponent, {
        data: { csvType: 'Assessment' },
      })
      .afterClosed()
      .pipe(
        tap((result: ExportAssessmentCsvDialogResult | undefined) => {
          if (!result) {
            return
          }
          const PAGE_SIZE = 500
          let offset = 0
          let allAssessments: {
            userName: string
            userEmail: string
            departmentName?: string | null
            reportToName?: string | null
            businessMeetingTitle: string
            accountName?: string | null
            businessMeetingRecordDate: Date
            summary?: string | null
            totalScore: number
            skillMapName: string
            businessMeetingId: string
            denominator?: number | null
            overallGrade?: string | null
            tagNames: string
          }[] = []
          this.loading = true

          const fetchNextPage = () => {
            this.assessmentStore
              .exportAssessmentSummaries(
                { limit: PAGE_SIZE, offset },
                {
                  startDate: result.startDate,
                  endDate: result.endDate,
                },
              )
              .pipe(
                tap((res) => {
                  assertGraphqlData(res)
                  const assessments = res.data.exportAssessmentSummaries
                  allAssessments = allAssessments.concat(assessments)

                  // PAGE_SIZE件未満しか返ってこなかったら終了
                  if (assessments.length < PAGE_SIZE) {
                    const hasDenominator = allAssessments.some(
                      (a) =>
                        a.denominator != null && a.denominator != undefined,
                    )
                    const hasTags = allAssessments.some(
                      (a) => a.tagNames !== '',
                    )
                    const headers: {
                      label: string
                      key: keyof (typeof allAssessments)[number]
                    }[] = hasDenominator
                      ? [
                          { label: '名前', key: 'userName' },
                          { label: 'メール', key: 'userEmail' },
                          { label: '部署', key: 'departmentName' },
                          { label: 'マネージャー', key: 'reportToName' },
                          { label: '商談', key: 'businessMeetingTitle' },
                          {
                            label: '商談日',
                            key: 'businessMeetingRecordDate',
                          },
                          { label: '顧客名', key: 'accountName' },
                          { label: 'サマリー', key: 'summary' },
                          { label: '総合スコア', key: 'totalScore' },
                          { label: 'スキルマップ名', key: 'skillMapName' },
                          { label: '商談ID', key: 'businessMeetingId' },
                          { label: '分母', key: 'denominator' },
                          { label: 'ランク', key: 'overallGrade' },
                        ]
                      : [
                          { label: '名前', key: 'userName' },
                          { label: 'メール', key: 'userEmail' },
                          { label: '部署', key: 'departmentName' },
                          { label: 'マネージャー', key: 'reportToName' },
                          { label: '商談', key: 'businessMeetingTitle' },
                          {
                            label: '商談日',
                            key: 'businessMeetingRecordDate',
                          },
                          { label: '顧客名', key: 'accountName' },
                          { label: 'サマリー', key: 'summary' },
                          { label: '総合スコア', key: 'totalScore' },
                          { label: 'スキルマップ名', key: 'skillMapName' },
                          { label: '商談ID', key: 'businessMeetingId' },
                          { label: 'ランク', key: 'overallGrade' },
                        ]
                    if (hasTags) {
                      headers.push({ label: 'タグ', key: 'tagNames' })
                    }
                    this.csvExportService.downloadCsv(
                      'assessment_summaries.csv',
                      headers,
                      allAssessments,
                    )
                    this.loading = false
                  } else {
                    // 次のページを取得（負荷低減のため1秒間隔を空ける）
                    offset += PAGE_SIZE
                    setTimeout(() => {
                      fetchNextPage()
                    }, 1000)
                  }
                }),
                catchError(() => {
                  this.loading = false
                  return of()
                }),
              )
              .subscribe()
          }

          fetchNextPage()
        }),
      )
      .subscribe()
  }

  exportIntegratedAssessments() {
    this.matDialog
      .open<
        ExportIntegratedAssessmentComponent,
        unknown,
        ExportIntegratedAssessmentDialogResult
      >(ExportIntegratedAssessmentComponent, {
        width: '640px',
        maxWidth: 'calc(100vw - 32px)',
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(
          (result): result is ExportIntegratedAssessmentDialogResult =>
            !!result,
        ),
        tap(() => {
          this.exportingIntegrated = true
          this.exportSnackBarRef = this.snackBar.open(
            'エクスポート中です。しばらくお待ちください…',
            '閉じる',
            { duration: 60000 },
          )
        }),
        switchMap((result) =>
          this.integratedAssessmentExportService
            .exportIntegratedAssessments({
              startDate: result.startDate,
              endDate: result.endDate,
              skillMapIds: result.skillMapIds,
            })
            .pipe(
              catchError((err: unknown) =>
                this.getExportErrorMessage(err).pipe(
                  tap((message) =>
                    this.messageDialogService.showError(message).subscribe(),
                  ),
                  ignoreElements(),
                ),
              ),
              finalize(() => {
                this.exportSnackBarRef?.dismiss()
                this.exportSnackBarRef = null
                this.exportingIntegrated = false
              }),
            ),
        ),
        tap((response) => {
          const contentDisposition = response.headers.get('Content-Disposition')
          const fileName = getFileNameFromContentDisposition(
            contentDisposition,
            'integrated_assessments.xlsx',
          )
          if (response.body) {
            downloadBlob(response.body, fileName)
          }
        }),
      )
      .subscribe()
  }

  selectCellClick(event: Event) {
    event.stopPropagation()
  }

  checkAllAssessments = (checked: boolean) => {
    let selectedCount = this.dataSource.filter((a) => a.selected).length
    this.dataSource.forEach((assessment) => {
      if (checked && !assessment.selected) {
        if (
          selectedCount < MAXIMUM_SELECTION_COUNT &&
          this.isDeletableAssessment(assessment)
        ) {
          assessment.selected = checked
          selectedCount += 1
        }
      } else if (!checked && assessment.selected) {
        assessment.selected = checked
      }
    })
    if (checked && selectedCount > MAXIMUM_SELECTION_COUNT) {
      this.messageDialogService.showInfo(
        `一度に選択できるのは ${MAXIMUM_SELECTION_COUNT} 件までです。`,
      )
    }
  }

  checkChanged = (checkbox: MatCheckbox, assessment: AssessmentRow) => {
    const selectedCount = this.dataSource.filter((a) => a.selected)?.length ?? 0
    if (checkbox.checked && selectedCount >= MAXIMUM_SELECTION_COUNT) {
      this.messageDialogService
        .showInfo(
          `一度に選択できるのは ${MAXIMUM_SELECTION_COUNT} 件までです。`,
        )
        .pipe(
          finalize(() => {
            checkbox.checked = false
          }),
        )
        .subscribe()
      return
    }

    assessment.selected = !assessment.selected
  }

  showRenameBusinessMeeting(assessment: AssessmentSummary) {
    const dialogRef = this.matDialog.open(RenameBusinessMeetingComponent, {
      id: 'rename-business-meeting',
      disableClose: true,
      panelClass: ['unset-mat-dialog-padding'],
      autoFocus: false,
    })

    dialogRef.componentInstance.data = {
      assessmentId: assessment.id,
      name: assessment.businessMeeting.title,
    }

    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((newName) => {
        if (newName) {
          assessment.businessMeeting.title = newName
        }
      })
  }

  showEditAssessmentTags(assessment: AssessmentSummary): void {
    const tagMasters = this.tagItems.map((item) => {
      if (!item.id) {
        throw new Error('タグマスターIDが設定されていません')
      }

      return { id: item.id, name: item.label }
    })
    this.matDialog
      .open<
        EditAssessmentTagsComponent,
        EditAssessmentTagsDialogData,
        EditAssessmentTagsDialogResult
      >(EditAssessmentTagsComponent, {
        data: {
          assessmentId: assessment.id,
          tagMasters,
          tags: assessment.tags,
        },
        width: '480px',
        maxWidth: 'calc(100vw - 32px)',
        disableClose: true,
      })
      .afterClosed()
      .pipe(
        filter(
          (result): result is EditAssessmentTagsDialogResult =>
            result?.hasIssuedChanges === true,
        ),
        tap(() => {
          this.offset = 0
          this.dataSource = []
          this.loadAssessments()
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  deleteAssessmentsBulk = () => {
    const assessmentIds = this.dataSource
      .filter((a) => a.selected)
      .map((a) => a.id)

    if (assessmentIds.length === 0) {
      this.messageDialogService
        .showError('削除するアセスメントを選択してください。')
        .subscribe()
      return
    }

    this.messageDialogService
      .showConfirm(
        `本当に ${assessmentIds.length} 件のアセスメントを削除してもよろしいですか？`,
        {
          primaryButtonText: '削除',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(
        filter((result) => result === DialogResult.PrimaryButtonClicked),
        switchMap(() => {
          return this.assessmentStore.deleteAssessments(assessmentIds).pipe(
            tap(() => {
              this.offset = 0
              this.loadAssessments()
            }),
          )
        }),
        tap(() => {
          this.snackBar.open('削除しました', 'close', {
            duration: 3000,
          })
        }),
      )
      .subscribe()
  }

  contextMenuClick(type: string, assessment: AssessmentSummary) {
    if (type === 'RENAME') {
      this.showRenameBusinessMeeting(assessment)
    } else if (type === 'EDIT_TAGS') {
      this.showEditAssessmentTags(assessment)
    } else if (type === 'DELETE') {
      this.messageDialogService
        .showConfirm(
          `
          「${assessment.businessMeeting.title}」のアセスメントを削除してもよろしいですか？
          この操作は取り消すことができません。
          `,
          {
            primaryButtonText: '削除',
            secondaryButtonText: 'キャンセル',
          },
        )
        .pipe(
          filter((result) => result === DialogResult.PrimaryButtonClicked),
          switchMap(() => {
            this.loading = true
            return this.assessmentStore.deleteAssessment(assessment.id).pipe(
              catchError(() => {
                this.messageDialogService
                  .showError(
                    'アセスメントの削除に失敗しました。時間をおいて再度お試しください。',
                  )
                  .subscribe()
                return of()
              }),
            )
          }),
          tap(() => {
            this.offset = 0
            this.loadAssessments()
          }),
          finalize(() => (this.loading = false)),
        )
        .subscribe()
    }
  }

  isSelfUser(assessment: AssessmentSummary): boolean {
    return assessment.user?.id === this.authService.user?.id
  }

  isDirectManager(assessment: AssessmentSummary): boolean {
    return assessment.user?.reportTo?.id === this.authService.user?.id
  }

  isEnablerOrAdmin(): boolean {
    return (
      this.userStore
        .ctxUser()
        ?.roles.some(
          (role) => role.code === 'ENABLER' || role.code === 'SUPER_ADMIN',
        ) ?? false
    )
  }

  isDeletableAssessment(assessment: AssessmentSummary): boolean {
    return (
      this.isEnablerOrAdmin() ||
      this.isSelfUser(assessment) ||
      this.isDirectManager(assessment)
    )
  }

  /** タグマスターの取得完了を記録し、待機中の検索に通知する */
  private completeTagMastersLoading(): void {
    this.tagMastersLoaded = true
    this.tagMastersLoaded$.next()
    this.tagMastersLoaded$.complete()
  }

  /** URL の条件を復元し、タグ指定時だけマスター取得を待って検索する */
  private observeQueryParams(): void {
    this.route.queryParamMap
      .pipe(
        tap((queryParams) => {
          const queryName = queryParams.get('queryName') ?? ''
          // 自画面の入力を反映しただけの URL 更新では、入力中の文字を上書きしない
          if (
            (this.requestedQueryParams == null ||
              !areQueryParamsEqual(queryParams, this.requestedQueryParams)) &&
            this.searchInput.value !== queryName
          ) {
            // value セッターは changed を発火するが、デバウンス後の queryNameChange が
            // 同値を無視するため二重検索にならない。
            this.searchInput.value = queryName
          }
          this.queryName = queryName
          this.tagMatch =
            queryParams.get('tagMatch') === 'all'
              ? TagMatchMode.All
              : TagMatchMode.Any
          const reportTo = queryParams.get('reportTo')
          this.reportToUserId =
            reportTo != null && this.isValidReportTo(reportTo) ? reportTo : ''
          const sortField = queryParams.get('sortField')
          this.sortField =
            sortField != null && SORT_FIELDS.includes(sortField)
              ? sortField
              : DEFAULT_SORT_FIELD
          const sortDirection = queryParams.get('sortDirection')
          this.sortDirection =
            sortDirection === 'asc' || sortDirection === 'desc'
              ? sortDirection
              : DEFAULT_SORT_DIRECTION
        }),
        switchMap((queryParams) =>
          queryParams.has('tagId')
            ? this.tagMastersLoaded$.pipe(
                take(1),
                map(() => queryParams),
              )
            : of(queryParams),
        ),
        untilDestroyed(this),
      )
      .subscribe((queryParams) => {
        const tagIds = queryParams.getAll('tagId')
        this.tagItems = this.tagItems.map((item) => ({
          ...item,
          checked: item.id != null && tagIds.includes(item.id),
        }))
        const normalized = convertToParamMap(
          this.buildAssessmentListQueryParams(),
        )
        const conditionsChanged =
          this.lastQueryParams == null ||
          !areQueryParamsEqual(normalized, this.lastQueryParams)
        this.updateQueryParams(queryParams)
        if (conditionsChanged) {
          this.offset = 0
          this.dataSource = []
          this.loadAssessments()
        }
      })
  }

  /** 初期値を省略して現在の条件を URL 向けに組み立てる */
  private buildAssessmentListQueryParams(): Params {
    const queryParams: Params = {}
    if (this.reportToUserId !== '') {
      queryParams['reportTo'] = this.reportToUserId
    }
    if (this.queryName !== '') {
      queryParams['queryName'] = this.queryName
    }
    // マスター取得前のタグ条件は、未復元の UI ではなく URL から引き継ぐ
    const tagIds = this.tagMastersLoaded
      ? this.selectedTagIds()
      : this.route.snapshot.queryParamMap.getAll('tagId')
    if (tagIds != null && tagIds.length > 0) {
      queryParams['tagId'] = tagIds
    }
    if (this.tagMatch === TagMatchMode.All) {
      queryParams['tagMatch'] = 'all'
    }
    if (this.sortField !== DEFAULT_SORT_FIELD) {
      queryParams['sortField'] = this.sortField
    }
    if (this.sortDirection !== DEFAULT_SORT_DIRECTION) {
      queryParams['sortDirection'] = this.sortDirection
    }
    return queryParams
  }

  /**
   * 同じ条件の再取得と履歴追加を避けて URL を更新する
   * @param restored URL から復元した直後のクエリ。画面操作による更新では省略する
   */
  private updateQueryParams(restored?: ParamMap): void {
    const queryParams = this.buildAssessmentListQueryParams()
    const normalized = convertToParamMap(queryParams)
    this.requestedQueryParams = normalized
    // 取得待ちの条件を検索済み扱いにすると、復元完了後の検索が省略される
    if (!this.waitingForTagMasters) {
      this.lastQueryParams = normalized
    }
    // 画面操作では常に遷移し、反映前の古い遷移を置き換えて条件の巻き戻りを防ぐ
    if (restored == null || !areQueryParamsEqual(normalized, restored)) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        replaceUrl: true,
      })
    }
  }

  /** 検索条件の変更を switchMap し、古い一覧リクエストを破棄する */
  private observeAssessmentSearches(): void {
    this.assessmentSearch$
      .pipe(
        switchMap(() => {
          this.loading = true
          const tagIds = this.selectedTagIds()
          return this.assessmentStore
            .searchAssessments(
              this.queryName,
              {
                limit: this.PAGE_SIZE_LIMIT,
                offset: this.PAGE_SIZE_LIMIT * this.offset,
              },
              {
                sortField: this.sortField,
                sortOrder: this.sortDirection,
              },
              tagIds,
              this.reportToUserId === '' ? null : this.reportToUserId,
              tagIds == null ? undefined : this.tagMatch,
            )
            .pipe(
              tap((assessments) => {
                if (this.offset === 0) {
                  this.dataSource = []
                  this.scrollTop()
                }
                this.dataSource = this.dataSource.concat(
                  assessments.map((assessment) =>
                    this.toAssessmentRow(assessment),
                  ),
                )
              }),
              finalize(() => (this.loading = false)),
            )
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  /** 現在の検索条件で一覧取得を開始する */
  private loadAssessments(): void {
    if (this.waitingForTagMasters) {
      return
    }
    this.assessmentSearch$.next()
  }

  /** URL のタグ条件の復元にマスター取得が必要かを判定する */
  private get waitingForTagMasters(): boolean {
    return (
      !this.tagMastersLoaded && this.route.snapshot.queryParamMap.has('tagId')
    )
  }

  /** 選択済みタグの ID を検索変数向けに正規化する */
  private selectedTagIds(): string[] | undefined {
    const selectedItems = this.tagItems.filter((item) => item.checked)
    if (selectedItems.length === 0) {
      return undefined
    }

    return selectedItems.map((item) => {
      if (!item.id) {
        throw new Error('タグマスターIDが設定されていません')
      }
      return item.id
    })
  }

  /** 画面幅とタグ機能の有効状態に合わせて表示列を更新する */
  private updateDisplayedColumns(): void {
    if (this.isMobile) {
      this.displayedColumns = this.hasAssessmentTagMasters
        ? this.mobileColumnsWithTags
        : this.mobileColumns
      return
    }

    this.displayedColumns = this.hasAssessmentTagMasters
      ? this.desktopColumnsWithTags
      : this.desktopColumns
  }

  private scrollTop() {
    if (this.tableContainer?.nativeElement?.scrollTop) {
      this.tableContainer.nativeElement.scrollTop = 0
    }
  }

  private toAssessmentRow(assessment: AssessmentSummary): AssessmentRow {
    return {
      ...assessment,
      selected: false,
    }
  }

  /**
   * エクスポート API のエラーからユーザー向けメッセージを返す。
   * 400 はサーバーが日本語のバリデーションメッセージを返すためそのまま表示する。
   * 表示できない場合は 4xx / それ以外で汎用メッセージを出し分ける。再試行で解消しない
   * 4xx に「時間をおいて再度」を出すと、成功しない操作を促し続けることになるため。
   * responseType: 'blob' で呼んでいるためエラーボディは Blob で届く。
   */
  private getExportErrorMessage(err: unknown): Observable<string> {
    if (!(err instanceof HttpErrorResponse)) {
      return of(EXPORT_RETRIABLE_ERROR_MESSAGE)
    }

    const fallbackMessage =
      err.status >= 400 && err.status < 500
        ? EXPORT_CLIENT_ERROR_MESSAGE
        : EXPORT_RETRIABLE_ERROR_MESSAGE

    if (
      err.status !== HttpStatusCode.BadRequest ||
      !(err.error instanceof Blob)
    ) {
      return of(fallbackMessage)
    }

    return from(err.error.text()).pipe(
      map((text) => extractServerErrorMessage(text) ?? fallbackMessage),
      catchError(() => of(fallbackMessage)),
    )
  }
}

/** キー順や単一値・配列の違いに影響されずクエリを比較する */
function areQueryParamsEqual(a: ParamMap, b: ParamMap): boolean {
  return (
    a.keys.length === b.keys.length &&
    a.keys.every(
      (key) => JSON.stringify(a.getAll(key)) === JSON.stringify(b.getAll(key)),
    )
  )
}
